'use client'

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'

import { TasksClubOnlyDialog } from '@/app/[locale]/tasks/components/TasksClubOnlyDialog/TasksClubOnlyDialog'
import { TasksCompleteProfileDialog } from '@/app/[locale]/tasks/components/TasksCompleteProfileDialog/TasksCompleteProfileDialog'
import { TasksSignUpDialog } from '@/app/[locale]/tasks/components/TasksSignUpDialog/TasksSignUpDialog'
import {
  type FanProfileMissingField,
  isFanProfileCompleteForTasks,
  resolveMissingFanProfileFields
} from '@/features/tasks/fanProfileCompleteness'
import { isClubUserSession } from '@/features/tasks/fanTasksAccess'
import { useUserMe } from '@/hooks/useUserMe'
import { useAuthStore } from '@/stores/authStore'

type TaskTakeGateContextValue = {
  guardTaskTake: (onAllowed: () => void) => boolean
  isProfileLoading: boolean
}

const TaskTakeGateContext = createContext<TaskTakeGateContextValue | null>(null)

export function TaskTakeGateProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)
  const { data: userData, isLoading: isProfileLoading } = useUserMe()
  const [signUpOpen, setSignUpOpen] = useState(false)
  const [clubOnlyOpen, setClubOnlyOpen] = useState(false)
  const [completeProfileOpen, setCompleteProfileOpen] = useState(false)
  const [missingFields, setMissingFields] = useState<FanProfileMissingField[]>(
    []
  )

  const guardTaskTake = useCallback(
    (onAllowed: () => void): boolean => {
      if (isClubUserSession()) {
        setClubOnlyOpen(true)
        return false
      }

      if (!isAuthenticated) {
        setSignUpOpen(true)
        return false
      }

      if (isProfileLoading) {
        return false
      }

      const user = userData?.user
      if (!isFanProfileCompleteForTasks(user)) {
        setMissingFields(resolveMissingFanProfileFields(user))
        setCompleteProfileOpen(true)
        return false
      }

      onAllowed()
      return true
    },
    [isAuthenticated, isProfileLoading, userData?.user]
  )

  const value = useMemo(
    () => ({
      guardTaskTake,
      isProfileLoading
    }),
    [guardTaskTake, isProfileLoading]
  )

  return (
    <TaskTakeGateContext.Provider value={value}>
      {children}
      <TasksSignUpDialog open={signUpOpen} onOpenChange={setSignUpOpen} />
      <TasksClubOnlyDialog open={clubOnlyOpen} onOpenChange={setClubOnlyOpen} />
      <TasksCompleteProfileDialog
        open={completeProfileOpen}
        missingFields={missingFields}
        onOpenChange={setCompleteProfileOpen}
      />
    </TaskTakeGateContext.Provider>
  )
}

export function useTaskTakeGate() {
  const context = useContext(TaskTakeGateContext)
  if (!context) {
    throw new Error('useTaskTakeGate must be used within TaskTakeGateProvider')
  }
  return context
}

export function useOptionalTaskTakeGate() {
  return useContext(TaskTakeGateContext)
}
